import React, { useEffect } from "react";
import { authApiClient } from "../../api/authApiClient";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/Sidebar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { Skeleton } from "@/components/ui/Skeleton";

import { ChevronsUpDown, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CURRENT_USER } from "@/api/queries/currentUser";
import { useQuery } from "@apollo/client";

export const UserMenu = () => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(CURRENT_USER);
  const { open } = useSidebar();

  useEffect(() => {
    // FullStory.js - set currently logged in user data.
    if (data) {
      window.FS("setIdentity", {
        uid: data.me.uuid,
        properties: {
          displayName: data.me.name,
          email: data.me.email || "",
        },
      });
    }
  }, [data]);

  const handleSignout = async () => {
    await authApiClient.logout();
  };

  const imageUrl = data?.me?.profilePhoto?.url;
  const name = data?.me?.name;
  const email = data?.me?.email;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          {(() => {
            if (loading) return <UserMenuSkeleton />;

            return (
              <>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    id="user-menu"
                    size="lg"
                    data-testid="user-menu"
                    aria-label={t("nav.userMenu")}
                  >
                    <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent-dark">
                      {imageUrl && (
                        <img alt={name} className="rounded-md" src={imageUrl} />
                      )}
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate text-sm/3.5 font-semibold">
                        {name}
                      </span>
                      <span className="text-xs/3">{email}</span>
                    </div>
                    <ChevronsUpDown aria-hidden="true" className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side={open ? "bottom" : "right"}
                  align={open ? "center" : "end"}
                  className="min-w-[180px] rounded-md border bg-white p-1 shadow-md dark:bg-popover"
                >
                  {/* <DropdownMenuItem asChild>
                    <NavLink to={"/profile"}>{t("nav.profile")}</NavLink>
                  </DropdownMenuItem>  commented out for now */}
                  <DropdownMenuItem onClick={handleSignout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t("nav.logout")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </>
            );
          })()}
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

const UserMenuSkeleton = () => {
  return (
    <SidebarMenuButton size="lg" asChild>
      <div>
        <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
          <Skeleton className="h-8 w-8 bg-sidebar-accent-dark" />
        </div>
        <div className="w-full pt-[2px] pr-6">
          <Skeleton className="mb-[2px] h-[14px] w-[100px] bg-sidebar-accent-dark" />
          <Skeleton className="h-3 w-full bg-sidebar-accent-dark" />
        </div>
      </div>
    </SidebarMenuButton>
  );
};
